import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  Timestamp,
  writeBatch,
  doc
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Get user IDs based on target audience and segments
 */
export async function getTargetUserIds(
  targetAudience: 'all' | 'customers' | 'technicians' | 'specific',
  segments?: {
    inactiveDays?: number;
    roles?: Array<'admin' | 'technician' | 'customer'>;
    userIds?: string[]; // For single user or specific user IDs
  }
): Promise<string[]> {
  const usersRef = collection(db, 'users');
  let userIds: string[] = [];

  try {
    // If specific user IDs are provided, use those
    if (segments?.userIds && segments.userIds.length > 0) {
      return segments.userIds;
    }

    if (targetAudience === 'all') {
      // Get all users
      const allUsersQuery = query(usersRef);
      const snapshot = await getDocs(allUsersQuery);
      snapshot.forEach(doc => userIds.push(doc.id));
    } else if (targetAudience === 'customers') {
      // Get only customers
      const customersQuery = query(usersRef, where('role', '==', 'customer'));
      const snapshot = await getDocs(customersQuery);
      snapshot.forEach(doc => userIds.push(doc.id));
    } else if (targetAudience === 'technicians') {
      // Get only technicians
      const techniciansQuery = query(usersRef, where('role', '==', 'technician'));
      const snapshot = await getDocs(techniciansQuery);
      snapshot.forEach(doc => userIds.push(doc.id));
    } else if (targetAudience === 'specific' && segments?.roles && segments.roles.length > 0) {
      // For specific segments, query each role and combine
      const roleQueries = segments.roles.map(role => 
        query(usersRef, where('role', '==', role))
      );
      const allResults = await Promise.all(
        roleQueries.map(q => getDocs(q))
      );
      const userIdSet = new Set<string>();
      allResults.forEach(snapshot => {
        snapshot.forEach(doc => userIdSet.add(doc.id));
      });
      userIds = Array.from(userIdSet);
    }

    // TODO: Apply inactiveDays filter if specified
    // This would require checking lastSeenAt or similar field
    // For now, we'll skip this filter as it requires additional user data

    return userIds;
  } catch (error) {
    console.error('Error getting target user IDs:', error);
    throw error;
  }
}

/**
 * Create individual notification documents for each target user
 * Uses batching for efficiency (max 500 per batch)
 */
export async function createUserNotifications(
  targetUserIds: string[],
  notificationData: {
    type: string;
    title: string;
    body: string;
    data?: Record<string, any>;
  }
): Promise<number> {
  if (targetUserIds.length === 0) {
    console.log('No target users, skipping notification creation');
    return 0;
  }

  const notificationsRef = collection(db, 'notifications');
  const BATCH_SIZE = 500; // Firestore batch limit
  let createdCount = 0;

  try {
    // Process in batches
    for (let i = 0; i < targetUserIds.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchUserIds = targetUserIds.slice(i, i + BATCH_SIZE);
      
      batchUserIds.forEach(userId => {
        const notificationRef = doc(notificationsRef);
        batch.set(notificationRef, {
          userId,
          type: notificationData.type,
          title: notificationData.title,
          body: notificationData.body,
          data: notificationData.data || {},
          read: false,
          archived: false,
          createdAt: Timestamp.now(),
        });
      });

      await batch.commit();
      createdCount += batchUserIds.length;
      console.log(`Created ${batchUserIds.length} notifications (batch ${Math.floor(i / BATCH_SIZE) + 1})`);
    }

    console.log(`Successfully created ${createdCount} user notifications`);
    return createdCount;
  } catch (error) {
    console.error('Error creating user notifications:', error);
    throw error;
  }
}

/**
 * Get notification type based on context and target audience
 */
export function getNotificationType(
  targetAudience: string,
  context?: string
): string {
  if (context === 'booking') return 'booking_update';
  if (context === 'pool') return 'pool_invite';
  if (context === 'technician') return 'technician_update';
  if (context === 'payment') return 'payment_required';
  if (context === 'marketing') return 'system'; // Marketing notifications use system type
  return 'system'; // Default for admin broadcasts
}

/**
 * Create user notifications and system notification log
 * This is the main function to call when sending notifications from admin
 */
export async function sendNotificationToUsers(
  targetAudience: 'all' | 'customers' | 'technicians' | 'specific' | 'single',
  notificationData: {
    type?: string;
    title: string;
    body: string; // Note: body, not message
    data?: Record<string, any>;
    context?: string;
  },
  segments?: {
    inactiveDays?: number;
    roles?: Array<'admin' | 'technician' | 'customer'>;
    userIds?: string[]; // For single user or specific user IDs
  },
  sentBy?: string
): Promise<{
  userCount: number;
  systemNotificationId: string;
}> {
  try {
    // Get target user IDs
    // For 'single' audience, treat as 'specific' with userIds
    const effectiveAudience = targetAudience === 'single' ? 'specific' : targetAudience;
    const targetUserIds = await getTargetUserIds(effectiveAudience, segments);
    
    if (targetUserIds.length === 0) {
      throw new Error('No target users found for notification');
    }

    // Determine notification type
    const notificationType = notificationData.type || 
      getNotificationType(targetAudience, notificationData.context);

    // Create individual notifications for each user
    const createdCount = await createUserNotifications(targetUserIds, {
      type: notificationType,
      title: notificationData.title,
      body: notificationData.body,
      data: {
        ...notificationData.data,
        source: 'admin',
        sentBy: sentBy || 'admin',
      },
    });

    // Create system notification log (for admin history)
    const systemNotificationData = {
      title: notificationData.title,
      message: notificationData.body, // Keep "message" for admin log
      targetAudience,
      segments: segments || {},
      sentAt: Timestamp.now(),
      status: 'sent' as const,
      sentBy: sentBy || 'admin',
      userCount: createdCount,
      notificationType: notificationType,
    };

    const systemNotificationsRef = collection(db, 'systemNotifications');
    const systemNotificationRef = await addDoc(systemNotificationsRef, systemNotificationData);

    return {
      userCount: createdCount,
      systemNotificationId: systemNotificationRef.id,
    };
  } catch (error) {
    console.error('Error sending notification to users:', error);
    throw error;
  }
}

